import { getSite, getSiteMediaCount, getSiteUserCount } from '@jwplayer/companion/sites';
import { Site } from '@jwplayer/companion/sites/types';
import { GetOutput } from '@jwplayer/companion-gateway';
import { useMemo, useState } from 'react';
import { QueryConfig, useQuery } from 'react-query';

import { SessionStore } from '/global-state';
import { useSession } from '/global-state/session/hooks';
import { useAbortController } from '/module/hooks';

type SessionSite = GetOutput<'dashboard-scope', 'read'>['sites'][number];

export function useCurrentSiteId() {
  return SessionStore.useState((s) => s.currentSiteId);
}

export function useCurrentSiteIdOrThrow() {
  const siteId = SessionStore.useState((s) => s.currentSiteId);
  if (!siteId) {
    throw new Error('Missing siteId');
  }
  return siteId;
}

export function useCurrentSite() {
  return useSite(useCurrentSiteId());
}

export function useSite(siteId?: string | { siteId?: string }, options?: QueryConfig<Site, Error>) {
  const abortController = useAbortController();
  const _siteId = typeof siteId === 'object' ? siteId.siteId : siteId;
  if (!_siteId) {
    throw new Error('Unknown siteId!');
  }

  // Set default options
  options = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    ...options,
  };

  const cacheId = ['site', _siteId];

  return useQuery<Site, Error>(
    cacheId,
    async () => {
      return (await getSite(_siteId, { abortController })).content;
    },
    options,
  );
}

export function useSiteUserCount(siteId?: string | { siteId?: string }, options?: QueryConfig<number, Error>) {
  const abortController = useAbortController();
  const _siteId = typeof siteId === 'object' ? siteId.siteId : siteId;

  if (!_siteId) return;

  const cacheId = ['site-user-count', _siteId];

  // Set default options
  options = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    ...options,
  };

  return useQuery<number, Error>(
    cacheId,
    async () => {
      return (await getSiteUserCount([_siteId], { abortController })).content[_siteId];
    },
    options,
  );
}

export function useSiteMediaCount(siteId?: string | { siteId?: string }, options?: QueryConfig<number, Error>) {
  const abortController = useAbortController();
  const _siteId = typeof siteId === 'object' ? siteId.siteId : siteId;
  if (!_siteId) return;

  const cacheId = ['site-media-count', _siteId];

  // Set default options
  options = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    ...options,
  };

  return useQuery<number, Error>(
    cacheId,
    async () => {
      return (await getSiteMediaCount([_siteId], { abortController })).content[_siteId];
    },
    options,
  );
}

export function useSiteState<AllSitesOption extends boolean = false>(
  defaultSite: 'CURRENT_SITE' | null = 'CURRENT_SITE',
) {
  const { sites } = useSession();
  const currentSiteId = useCurrentSiteIdOrThrow();
  const currentSite = sites.find((s) => s.site_id === currentSiteId);
  if (!currentSite) {
    throw new Error('Could not find current site');
  }

  // Default to current site
  const [selectedSite, setSelectedSite] = useState<null | SessionSite>(defaultSite === null ? null : currentSite);

  const setSite = useMemo(
    () => (id: null | string) => {
      if (id === null) {
        setSelectedSite(null);
        return;
      }

      const foundSite = sites.find((site) => site.site_id === id);
      if (!foundSite) {
        throw new Error('Site id could not be found!');
      }

      setSelectedSite(foundSite);
    },
    [sites],
  );

  return [selectedSite, setSite] as AllSitesOption extends true
    ? [SessionSite | null, (id: string | null) => void]
    : [SessionSite, (id: string) => void];
}
